import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { fetchMe, qk } from "../api/hooks";
import { LoginPage } from "../pages/LoginPage";
import { WorkspaceListPage } from "../pages/WorkspaceListPage";
import { WorkspaceHomePage } from "../pages/WorkspaceHomePage";
import { PageEditorPage } from "../pages/PageEditorPage";
import { GraphExplorerPage } from "../graph/GraphExplorerPage";
import { ReviewInboxPage } from "../review/ReviewInboxPage";
import { WorkspaceSettingsPage } from "../pages/WorkspaceSettingsPage";
import { ServerSettingsPage } from "../server/ServerSettingsPage";
import { AccountPage } from "../account/AccountPage";
import { AppShell } from "./AppShell";

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: () => <Outlet /> });

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });

/** Auth guard (spec §8.2 app/auth guard): everything under this layout needs a session. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: async ({ context, location }) => {
    let me: Awaited<ReturnType<typeof fetchMe>>;
    try {
      // a cached "no session" is never trusted: a sign-in that happened outside the SPA — the
      // Keycloak redirect round trip (spec §19.1) — must be seen on the next navigation
      me = context.queryClient.getQueryData(qk.me)
        ? await context.queryClient.ensureQueryData({ queryKey: qk.me, queryFn: fetchMe, staleTime: 60_000 })
        : await context.queryClient.fetchQuery({ queryKey: qk.me, queryFn: fetchMe, staleTime: 0 });
    } catch {
      // offline: keep the restored session if there is one (spec §16.2 step 2)
      me = context.queryClient.getQueryData(qk.me) ?? null;
    }
    if (!me) throw redirect({ to: "/login", search: { next: location.href } });
    return { me };
  },
  component: AppShell,
});

const indexRoute = createRoute({ getParentRoute: () => authedRoute, path: "/", component: WorkspaceListPage });
const wsRoute = createRoute({ getParentRoute: () => authedRoute, path: "/w/$workspaceId", component: WorkspaceHomePage });
const pageRoute = createRoute({ getParentRoute: () => authedRoute, path: "/w/$workspaceId/d/$docId", component: PageEditorPage });
const graphRoute = createRoute({ getParentRoute: () => authedRoute, path: "/w/$workspaceId/graph", component: GraphExplorerPage });
const reviewRoute = createRoute({ getParentRoute: () => authedRoute, path: "/w/$workspaceId/review", component: ReviewInboxPage });
// [M26] server-wide settings for administrators (spec §8.26); not /admin, which is Django's
// [M28] the account page with the MCP connector (spec §8.27)
const accountRoute = createRoute({ getParentRoute: () => authedRoute, path: "/account", component: AccountPage });
const serverSettingsRoute = createRoute({ getParentRoute: () => authedRoute, path: "/settings", component: ServerSettingsPage });
const settingsRoute = createRoute({ getParentRoute: () => authedRoute, path: "/w/$workspaceId/settings", component: WorkspaceSettingsPage });

const routeTree = rootRoute.addChildren([loginRoute, authedRoute.addChildren([indexRoute, wsRoute, pageRoute, graphRoute, reviewRoute, settingsRoute, serverSettingsRoute, accountRoute])]);

export const router = createRouter({ routeTree, context: { queryClient: undefined! }, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
